import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  FileMagnifyingGlass,
  MagnifyingGlass,
  FileText,
  CheckCircle,
  Clock,
  WarningCircle
} from '@phosphor-icons/react';

const OCR_BADGE = {
  pending: 'secondary',
  processing: 'secondary',
  completed: 'default',
  failed: 'destructive'
};

export default function DocumentSearchPage() {
  const [q, setQ] = useState('');
  const [ocrStatus, setOcrStatus] = useState('all');
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, failed: 0 });
  const [loading, setLoading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [ocrText, setOcrText] = useState('');

  const loadStats = async () => {
    try {
      const res = await api.get('/documents/stats');
      setStats(res.data);
    } catch (err) {
      // Non-blocking
    }
  };

  const searchDocuments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (ocrStatus !== 'all') params.ocr_status = ocrStatus;

      const res = await api.get('/documents/search', { params });
      setDocuments(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to search documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    searchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    searchDocuments();
  };

  const openOcrEditor = (doc) => {
    setSelectedDoc(doc);
    setOcrText(doc.ocr_text || '');
  };

  const saveOcr = async () => {
    if (!selectedDoc) return;

    try {
      await api.post(`/documents/${selectedDoc.id}/ocr-placeholder`, {
        ocr_status: 'completed',
        ocr_text: ocrText,
        ocr_entities: {}
      });
      toast.success('OCR text saved');
      setSelectedDoc(null);
      setOcrText('');
      await loadStats();
      await searchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save OCR text');
    }
  };

  return (
    <div className="space-y-6" data-testid="document-search-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-primary flex items-center gap-2">
          <FileMagnifyingGlass size={28} />
          Document Search / OCR
        </h1>
        <p className="text-sm text-muted-foreground">
          Search uploaded request documents by file name, request number, vendor, OCR text, and document metadata.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <FileText size={24} />
            <div>
              <div className="text-xs text-muted-foreground">Total Documents</div>
              <div className="text-xl font-bold">{stats.total}</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Clock size={24} />
            <div>
              <div className="text-xs text-muted-foreground">Pending OCR</div>
              <div className="text-xl font-bold">{stats.pending}</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle size={24} />
            <div>
              <div className="text-xs text-muted-foreground">OCR Completed</div>
              <div className="text-xl font-bold">{stats.completed}</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <WarningCircle size={24} />
            <div>
              <div className="text-xs text-muted-foreground">OCR Failed</div>
              <div className="text-xl font-bold">{stats.failed}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Search Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitSearch} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-7">
              <Label>Search</Label>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search invoice no., OR no., vendor, request code, file name, OCR text..."
                  className="pl-9"
                  data-testid="document-search-input"
                />
              </div>
            </div>

            <div className="md:col-span-3">
              <Label>OCR Status</Label>
              <Select value={ocrStatus} onValueChange={setOcrStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {selectedDoc && (
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle>OCR Text Editor: {selectedDoc.file_name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              className="min-h-40"
              value={ocrText}
              onChange={(e) => setOcrText(e.target.value)}
              placeholder="Paste OCR text here for now. Automated OCR engine will populate this later."
            />
            <div className="flex gap-2">
              <Button onClick={saveOcr}>Save OCR Text</Button>
              <Button variant="outline" onClick={() => setSelectedDoc(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Results</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">File</th>
                  <th className="text-left py-2">Request</th>
                  <th className="text-left py-2">Vendor</th>
                  <th className="text-left py-2">Uploaded By</th>
                  <th className="text-left py-2">OCR</th>
                  <th className="text-left py-2">Uploaded</th>
                  <th className="text-left py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.id} className="border-b align-top">
                    <td className="py-3">
                      <div className="font-medium">{doc.file_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {doc.file_type || 'Unknown type'} · {doc.file_size || 0} bytes
                      </div>
                      {doc.ocr_text && (
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-2 max-w-md">
                          {doc.ocr_text}
                        </div>
                      )}
                    </td>
                    <td className="py-3">
                      {doc.request_id ? (
                        <Link to={`/payments/${doc.request_id}`} className="text-blue-600 hover:underline">
                          {doc.request_code || 'Open Request'}
                        </Link>
                      ) : '-'}
                      <div className="text-xs text-muted-foreground">{doc.request_status}</div>
                    </td>
                    <td className="py-3">{doc.vendor_name || '-'}</td>
                    <td className="py-3">{doc.uploaded_by_name || '-'}</td>
                    <td className="py-3">
                      <Badge variant={OCR_BADGE[doc.ocr_status] || 'secondary'}>
                        {doc.ocr_status || 'pending'}
                      </Badge>
                    </td>
                    <td className="py-3">
                      {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString() : '-'}
                    </td>
                    <td className="py-3">
                      <Button size="sm" variant="outline" onClick={() => openOcrEditor(doc)}>
                        OCR Text
                      </Button>
                    </td>
                  </tr>
                ))}

                {!documents.length && (
                  <tr>
                    <td colSpan="7" className="py-8 text-center text-muted-foreground">
                      No documents found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
